import LightCustomButton from "@components/buttons/LightCustomButton";
import { CurrencyLogoDropdown } from "@components/dropdowns/CurrencyLogoDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { invoiceServices } from "@services/apis/Invoice";
import {
  GetOfframpChargesData,
  UpdateOfframpChargeRequest,
} from "@services/models/InvoiceApprovals/GetOfframpChargesModel";
import { GetSupportedBaseCurrencies } from "@services/models/InvoiceApprovals/GetSupportedBaseCurrencies";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface EditOfframpChargeSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onChargeUpdated: () => void;
  chargeData: GetOfframpChargesData | null;
}

const EditOfframpChargeSlider: React.FC<EditOfframpChargeSliderProps> = ({
  isOpen,
  onClose,
  onChargeUpdated,
  chargeData,
}) => {
  const { handleSuccess, handleError } = useSnackbarMessage();

  const [chargePercentage, setChargePercentage] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [changeReason, setChangeReason] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [action, setAction] = useState<"update" | "activate" | "deactivate">(
    "update"
  );
  const [supportedOffRampCurrencies, setSupportedOffRampCurrencies] = useState<
    GetSupportedBaseCurrencies[]
  >([]);
  const [offRampCurrenciesLoading, setOffRampCurrenciesLoading] =
    useState<boolean>(true);
  const [selectedOffRampCurrency, setSelectedOffRampCurrency] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    if (chargeData) {
      setChargePercentage(chargeData.chargePercentage.toString());
      setDescription(chargeData.description || "");
      setChangeReason("");
      setAction("update");
      setSelectedOffRampCurrency(chargeData.currencyId);
    }
  }, [chargeData]);

  const handleSubmit = async () => {
    if (!chargeData) return;

    if (action === "update") {
      if (!chargePercentage.trim()) {
        handleError({ message: "Please enter charge percentage" });
        return;
      }

      const chargeValue = Number(chargePercentage);
      if (isNaN(chargeValue) || chargeValue < 0 || chargeValue > 100) {
        handleError({ message: "Charge percentage must be between 0 and 100" });
        return;
      }
    }

    try {
      setIsLoading(true);

      if (action === "update") {
        const requestData: UpdateOfframpChargeRequest = {
          chargeId: chargeData.chargeId,
          chargePercentage: Number(chargePercentage),
          description: description.trim() || undefined,
          changeReason: changeReason.trim() || undefined,
          currencyId: selectedOffRampCurrency,
          currencySymbol:
            supportedOffRampCurrencies.find(
              (currency) => currency.currencyId === selectedOffRampCurrency
            )?.currencySymbol || undefined,
        };

        await invoiceServices.updateOfframpCharge(requestData);
        handleSuccess({ message: "Offramp charge updated successfully!" });
      } else if (action === "activate") {
        await invoiceServices.activateOfframpCharge({
          chargeId: chargeData.chargeId,
          changeReason: changeReason.trim() || undefined,
        });
        handleSuccess({ message: "Offramp charge activated successfully!" });
      } else if (action === "deactivate") {
        await invoiceServices.deactivateOfframpCharge({
          chargeId: chargeData.chargeId,
          changeReason: changeReason.trim() || undefined,
        });
        handleSuccess({ message: "Offramp charge deactivated successfully!" });
      }

      resetForm();

      if (onChargeUpdated) {
        onChargeUpdated();
      }

      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Failed to update offramp charge";
      handleError({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSupportedOffRampCurrencies = async () => {
    try {
      setOffRampCurrenciesLoading(true);
      const response = await invoiceServices.getSupportedBaseCurrenciesList({
        isOffRampSupported: 1,
      });
      setSupportedOffRampCurrencies(response.data);
      setOffRampCurrenciesLoading(false);
    } catch (error) {
      setOffRampCurrenciesLoading(false);
    }
  };

  const handleOffRampSettlementCurrencySelection = (value: string | number) => {
    setSelectedOffRampCurrency(Number(value));
  };

  const resetForm = () => {
    setChargePercentage("");
    setDescription("");
    setChangeReason("");
    setAction("update");
    setSelectedOffRampCurrency(undefined);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    } else {
      fetchSupportedOffRampCurrencies();
    }
  }, [isOpen]);

  const isFormValid =
    action === "update"
      ? Boolean(chargePercentage.trim() && selectedOffRampCurrency)
      : true;

  if (!chargeData) return null;

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading={`Edit Offramp Charge (ID: ${chargeData.chargeId})`}
      onClose={() => {
        resetForm();
        onClose();
      }}
      onClickCross={() => {
        resetForm();
        onClose();
      }}
      showConfirm={false}
      isSlider={true}
      widthDesktop={"35%"}
    >
      <div className="flex flex-col px-[16px] py-[16px] mt-[60px]">
        <div className="flex flex-col gap-4 bg-bg-base rounded-lg p-4 border border-border-subtle">
          {/* Charge Info */}
          <div className="flex flex-col text-text-base text-sm space-y-2">
            <div>
              <strong>Merchant ID:</strong>{" "}
              {chargeData.merchantId || "Default (All Merchants)"}
            </div>
            <div>
              <strong>Current Charge:</strong> {chargeData.chargePercentage}%
            </div>
            <div>
              <strong>Currency:</strong> {chargeData.currencySymbol} (
              {chargeData.currencyName})
            </div>
            <div>
              <strong>Status:</strong>{" "}
              {chargeData.isActive ? "Active" : "Inactive"}
            </div>
          </div>

          {/* Action Selection */}
          <div>
            <label className="block text-sm font-medium text-text-base mb-3">
              Select Action
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setAction("update")}
                className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  action === "update"
                    ? "bg-bg-button-secondary border border-border-button-primary text-white shadow-sm"
                    : "bg-bg-base border border-border-subtle text-text-base hover:bg-bg-page-body hover:border-border-button-secondary"
                }`}
              >
                Update
              </button>
              <button
                type="button"
                onClick={() => setAction("activate")}
                disabled={chargeData.isActive}
                className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  action === "activate"
                    ? "bg-bg-status-green border border-bg-helper-green-dark text-white shadow-sm"
                    : chargeData.isActive
                    ? "bg-bg-disabled border border-border-disabled text-text-disabled cursor-not-allowed"
                    : "bg-bg-base border border-border-subtle text-text-base hover:bg-bg-page-body hover:border-bg-helper-green-light"
                }`}
              >
                Activate
              </button>
              <button
                type="button"
                onClick={() => setAction("deactivate")}
                disabled={!chargeData.isActive}
                className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  action === "deactivate"
                    ? "bg-bg-status-red border border-border-button-delete text-white shadow-sm"
                    : !chargeData.isActive
                    ? "bg-bg-disabled border border-border-disabled text-text-disabled cursor-not-allowed"
                    : "bg-bg-base border border-border-subtle text-text-base hover:bg-bg-page-body hover:border-bg-helper-red-dark"
                }`}
              >
                Deactivate
              </button>
            </div>
          </div>

          {action === "update" && (
            <>
              <GlobalTextInputField
                placeholder="Enter charge percentage (0-100)"
                value={chargePercentage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setChargePercentage(e.target.value)
                }
                title="Charge Percentage"
              />

              <GlobalTextInputField
                placeholder="Enter description for this charge"
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDescription(e.target.value)
                }
                title="Description (Optional)"
              />

              {!offRampCurrenciesLoading ? (
                <CurrencyLogoDropdown
                  listOfValues={supportedOffRampCurrencies.map(
                    (supportedOffRampCurrency) => ({
                      id: supportedOffRampCurrency.currencyId,
                      currency: supportedOffRampCurrency.currencySymbol,
                      currencyLogo: supportedOffRampCurrency.currencyImg,
                      currencyName: supportedOffRampCurrency.currencyName,
                      isToken: true,
                      isReceive: true,
                    })
                  )}
                  enableSearch={true}
                  searchableValues={supportedOffRampCurrencies.map((value) => [
                    value.currencySymbol,
                    value.currencyName,
                  ])}
                  value={selectedOffRampCurrency ?? undefined}
                  onChange={handleOffRampSettlementCurrencySelection}
                  label="Select Final Settlement Currency*"
                  color="bg-main-container-header"
                  baseColor="bg-bg-page-body"
                  expandedBorderColor="border-button-primary"
                  borderColor="border-subtle"
                />
              ) : (
                <Skeleton height={44} className="mb-1" />
              )}
            </>
          )}

          <GlobalTextInputField
            placeholder="Enter reason for this change"
            value={changeReason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setChangeReason(e.target.value)
            }
            title="Change Reason (Optional)"
          />

          <LightCustomButton
            text={
              action === "update"
                ? "UPDATE CHARGE"
                : action === "activate"
                ? "ACTIVATE CHARGE"
                : "DEACTIVATE CHARGE"
            }
            onClick={handleSubmit}
            isActive={isFormValid && !isLoading}
            isButtonLoading={isLoading}
            className="w-full"
          />
        </div>
      </div>
    </SlidingPanel>
  );
};

export default EditOfframpChargeSlider;
